import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import GenericStatusLoading from 'components/GenericStatusLoading'
import ErrorScreen from 'components/ErrorScreen'
import BackButton from 'components/BackButton'
import { STAGE_PERSONAL } from 'containers/Checkout/constants'
import { transitionToStage } from 'containers/Checkout/actions'
import { handleTrackingOnBlur, handleTrackingOnChange } from 'hoc/WithInputAnalytics/utils'
import {
  TEXT_INTERNATIONAL_VISA,
  TEXT_INTERNATIONAL_VISA_VERIFIED,
  TEXT_INTERNATIONAL_VISA_PENDING,
  TEXT_INTERNATIONAL_VISA_NO_MATCH,
  TEXT_INTERNATIONAL_VISA_LOCKED,
  TEXT_INTERNATIONAL_VISA_FAILED,
  TEXT_INTERNATIONAL_VISA_CONTRIB,
  TEXT_INTERNATIONAL_VISA_NO_EFFECT,
  GA_EVENT_CATEGORY,
} from './constants'
import { identificationInit, identificationComplete } from './actions'
import styles from './styles.scss'

class IdentificationStage extends PureComponent {
  static propTypes = {
    // From Wagtail
    greenidProduction: PropTypes.bool,
    greenidAccountID: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
    greenidAPICode: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
    title: PropTypes.string,
    text: PropTypes.string,
    termsText: PropTypes.string,

    // From Redux
    identificationVerificationToken: PropTypes.string, // eslint-disable-line react/no-unused-prop-types

    // Redux actions
    identificationInit: PropTypes.func,
    identificationComplete: PropTypes.func,
    transitionToStage: PropTypes.func,
  }

  static defaultProps = {
    greenidProduction: false,
    greenidAccountID: '',
    greenidAPICode: '',
    identificationVerificationToken: '',
    title: 'Verify Your Identity',
    text: '',
    termsText: 'I agree that my above information is checked with the Issuer or Official Record Holder',
    identificationInit: f => f,
    identificationComplete: f => f,
    transitionToStage: f => f,
  }

  constructor(props) {
    super(props)

    this.errorCallback = this.errorCallback.bind(this)
    this.sessionCompleteCallback = this.sessionCompleteCallback.bind(this)
    this.holder = null
    this.state = {
      simpleUILoaded: false,
      formComplete: false,
      hasFatalError: false, // needs page reload
    }
    this.fieldFocusTimes = {}
    this.analyticsTimeout = null
  }

  componentDidMount() {
    this.checkGreenIdLoaded()
  }

  componentWillReceiveProps(newProps) {
    const {
      greenidAccountID,
      greenidAPICode,
      identificationVerificationToken,
    } = newProps
    if (newProps.identificationVerificationToken && !this.state.simpleUILoaded) {
      window.greenidUI.setup({
        environment: this.props.greenidProduction ? 'prod' : 'test',
        frameId: 'greenid-holder',
        country: 'AU',
        sessionCompleteCallback: this.sessionCompleteCallback,
        errorCallback: this.errorCallback,
      })

      window.greenidUI.show(
        greenidAccountID,
        greenidAPICode,
        identificationVerificationToken,
      )
      this.setState({ simpleUILoaded: true }, () => {
        this.attachFormAnalytics()
      })
    }
  }

  componentWillUnmount() {
    if (this.holder) {
      this.holder.removeEventListener('blur', handleTrackingOnBlur)
      this.holder.removeEventListener('change', handleTrackingOnBlur)
    }
    if (this.analyticsTimeout) {
      clearTimeout(this.analyticsTimeout)
    }
  }

  onBack = () => {
    this.props.transitionToStage(STAGE_PERSONAL)
  }

  handleTrackingOnFocus = (e) => {
    const name = e.target.name || e.target.id
    let time = this.fieldFocusTimes[name]
    if (time) return time

    time = new Date().getTime()
    this.fieldFocusTimes[name] = time
    return time
  }

  attachFormAnalytics = () => {
    // Timeout required for GreenID to init
    const loading = !this.holder
      || !this.holder.querySelector('#greenid-source-body')
      || !this.holder.querySelector('#greenid-source-body').children.length
    if (loading) {
      this.analyticsTimeout = setTimeout(() => {
        this.attachFormAnalytics()
      }, 300)
      return
    }
    this.holder.addEventListener('focus', (event) => {
      if (event.target.matches('input[type="text"]')) {
        this.handleTrackingOnFocus(event)
      }
    }, true)
    this.holder.addEventListener('blur', (event) => {
      if (event.target.matches('input[type="text"]')) {
        handleTrackingOnBlur(event, GA_EVENT_CATEGORY, this.fieldFocusTimes[event.target.name || event.target.id])
      }
    }, true)
    this.holder.addEventListener('change', (event) => {
      if (event.target.matches('input[type="checkbox"]')) {
        handleTrackingOnChange(event, GA_EVENT_CATEGORY, this.fieldFocusTimes[event.target.name || event.target.id])
      }
    }, true)
  }

  checkGreenIdLoaded() {
    if (!window.greenidUI) {
      setTimeout(() => this.checkGreenIdLoaded(), 300)
    } else {
      // Overriding greenID Defaults
      window.greenidConfig.setOverrides({
        enable_save_and_complete_later: false,
        intro_title: `<h2>${this.props.title}</h2>`,
        onesourceleft_alert_text: '',
        intro_introText0: this.props.text,
        submit_button_text: 'Continue',
        modal_error_cancel_button: 'Back ',

        // Australian Visa overrides
        visadvs_short_title: TEXT_INTERNATIONAL_VISA,
        visadvs_title: TEXT_INTERNATIONAL_VISA,
        visadvs_verifiedStr: TEXT_INTERNATIONAL_VISA_VERIFIED,
        visadvs_pendingStr: TEXT_INTERNATIONAL_VISA_PENDING,
        visadvs_inProgressStr: TEXT_INTERNATIONAL_VISA_NO_MATCH,
        visadvs_lockedoutStr: TEXT_INTERNATIONAL_VISA_LOCKED,
        visadvs_failedStr: TEXT_INTERNATIONAL_VISA_FAILED,
        visadvs_notContributingStr: TEXT_INTERNATIONAL_VISA_CONTRIB,
        visadvs_notContributingStr_NC_DVS_D: TEXT_INTERNATIONAL_VISA_NO_EFFECT,

        // terms and conditions
        visadvs_tandc_label: this.props.termsText,
        citizenshipcertificatedvs_tandc_label: this.props.termsText,
        passportdvs_tandc_label: this.props.termsText,
        medicaredvs_tandc_label: this.props.termsText,

        /* All The Drivers Regos */
        actregodvs_tandc_label: this.props.termsText,
        nswregodvs_tandc_label: this.props.termsText,
        qldregodvs_tandc_label: this.props.termsText,
        vicregodvs_tandc_label: this.props.termsText,
        saregodvs_tandc_label: this.props.termsText,
        waregodvs_tandc_label: this.props.termsText,
        ntregodvs_tandc_label: this.props.termsText,
      })

      this.props.identificationInit()
    }
  }

  errorCallback(verificationToken, errorName) {
    if (errorName === 'InvalidTokenFault' || errorName === 'TokenTimeoutFault') {
      this.setState({ hasFatalError: true })
    }
    // sometimes user could have verified before or token already expires,
    // so we ping backend and check whether we should proceed
    this.props.identificationComplete(verificationToken)
  }

  sessionCompleteCallback(verificationToken) {
    this.setState({ formComplete: true })
    this.props.identificationComplete(verificationToken)
  }

  render() {
    const greenidContainer = (
      <div>
        <div id="greenid-holder" className="greenid-holder" ref={(ref) => { this.holder = ref }} />
        <BackButton onClick={this.onBack} className={styles.backButton} />
      </div>
    )

    if (this.state.hasFatalError) {
      const errorText = '<h2>Something went wrong...</h2>' +
        '<p>Unfortunately, the verification token is invalid or already expired.</p>'
      return <ErrorScreen text={errorText} />
    }

    return (this.state.simpleUILoaded && !this.state.formComplete) ?
      greenidContainer : <GenericStatusLoading />
  }
}

function mapStateToProps(state) {
  return {
    identificationVerificationToken: state.checkoutReducer.identificationVerificationToken,
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      transitionToStage,
      identificationInit,
      identificationComplete,
    },
    dispatch
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(IdentificationStage)



// WEBPACK FOOTER //
// ./containers/stages/IdentificationStage/index.jsx